import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { MemoryRouter } from "react-router-dom";
import { contentRoutes } from "../routes/contentRoutes";

const STOP_WORDS = new Set([
    "a",
    "an",
    "and",
    "are",
    "as",
    "at",
    "be",
    "for",
    "from",
    "how",
    "i",
    "in",
    "is",
    "it",
    "of",
    "on",
    "or",
    "the",
    "to",
    "what",
    "where",
    "with",
]);

const EXCLUDED_SEARCH_PATHS = new Set([
    "/",
    "/whatsnew",
]);

let cachedSearchIndex = null;

function normalizeText(value = "") {
    return value
        .toLowerCase()
        .replace(/&/g, " and ")
        .replace(/[^a-z0-9\s/_-]/g, " ")
        .replace(/[/_-]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

function tokenize(value) {
    const normalizedValue = normalizeText(value);

    return normalizedValue ? normalizedValue.split(" ").filter(Boolean) : [];
}

function stemToken(token) {
    if (token.length <= 3) {
        return token;
    }

    if (token.endsWith("ies") && token.length > 4) {
        return `${token.slice(0, -3)}y`;
    }

    if (token.endsWith("ing") && token.length > 5) {
        return token.slice(0, -3);
    }

    if (token.endsWith("ed") && token.length > 4) {
        return token.slice(0, -2);
    }

    if (token.endsWith("es") && token.length > 4) {
        return token.slice(0, -2);
    }

    if (token.endsWith("s") && token.length > 3) {
        return token.slice(0, -1);
    }

    return token;
}

function hasPartialTokenMatch(token, tokens) {
    return tokens.some(
        (candidate) =>
            candidate.startsWith(token) ||
            token.startsWith(candidate) ||
            candidate.includes(token)
    );
}

function getQueryTokens(searchQuery) {
    const rawTokens = tokenize(searchQuery);

    if (!rawTokens.length) {
        return [];
    }

    const filteredTokens = rawTokens.filter(
        (token) => token.length > 1 && !STOP_WORDS.has(token)
    );

    if (filteredTokens.length > 0) {
        return filteredTokens;
    }

    return rawTokens.filter((token) => token.length > 1);
}

function formatPathTitle(path) {
    if (path === "/") {
        return "Home";
    }

    return path
        .split("/")
        .filter(Boolean)
        .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
        .join(" ");
}

function cleanText(value = "") {
    return value.replace(/\s+/g, " ").trim();
}

function getDocumentFromMarkup(markup) {
    const parser = new DOMParser();
    return parser.parseFromString(markup, "text/html");
}

function getSearchDocument(route) {
    const { path, Component } = route;
    const markup = renderToStaticMarkup(
        <MemoryRouter initialEntries={[path]}>
            <Component />
        </MemoryRouter>
    );
    const document = getDocumentFromMarkup(markup);
    const bodyText = cleanText(document.body.textContent || "");
    const title = cleanText(
        document.querySelector("h1")?.textContent || formatPathTitle(path)
    );
    const headings = cleanText(
        Array.from(document.querySelectorAll("h1, h2, h3"))
            .map((element) => element.textContent || "")
            .join(" ")
    );
    const excerpt = cleanText(
        document.querySelector("p")?.textContent || bodyText.slice(0, 220)
    );
    const titleTokens = tokenize(title);
    const headingTokens = tokenize(headings);
    const pathTokens = tokenize(path);
    const contentTokens = tokenize(bodyText);

    return {
        key: path,
        path,
        title,
        excerpt,
        headings,
        content: bodyText,
        normalizedTitle: normalizeText(title),
        normalizedHeadings: normalizeText(headings),
        normalizedPath: normalizeText(path),
        normalizedContent: normalizeText(bodyText),
        searchableText: normalizeText(`${title} ${headings} ${path} ${bodyText}`),
        titleTokens,
        headingTokens,
        pathTokens,
        contentTokens,
        titleTokenSet: new Set(titleTokens),
        headingTokenSet: new Set(headingTokens),
        pathTokenSet: new Set(pathTokens),
        contentTokenSet: new Set(contentTokens),
        titleStemSet: new Set(titleTokens.map(stemToken)),
        headingStemSet: new Set(headingTokens.map(stemToken)),
        pathStemSet: new Set(pathTokens.map(stemToken)),
        contentStemSet: new Set(contentTokens.map(stemToken)),
    };
}

function getSearchIndex() {
    if (cachedSearchIndex) {
        return cachedSearchIndex;
    }

    cachedSearchIndex = contentRoutes
        .filter(({ path }) => !EXCLUDED_SEARCH_PATHS.has(path))
        .map((route) => {
            try {
                return getSearchDocument(route);
            } catch (error) {
                return null;
            }
        })
        .filter(Boolean);

    return cachedSearchIndex;
}

function scorePage(page, queryTokens, normalizedQuery) {
    let score = 0;
    const matchedTokens = new Set();

    if (!normalizedQuery) {
        return score;
    }

    if (page.normalizedTitle === normalizedQuery) {
        score += 180;
    } else if (page.normalizedTitle.includes(normalizedQuery)) {
        score += 120;
    }

    if (page.normalizedHeadings.includes(normalizedQuery)) {
        score += 80;
    }

    if (page.normalizedPath === normalizedQuery) {
        score += 70;
    } else if (page.normalizedPath.includes(normalizedQuery)) {
        score += 35;
    }

    if (page.normalizedContent.includes(normalizedQuery)) {
        score += 30;
    }

    for (let i = 0; i < queryTokens.length; i++) {
        const token = queryTokens[i];
        const tokenStem = stemToken(token);
        let tokenMatched = false;

        if (page.titleTokenSet.has(token)) {
            score += 36;
            tokenMatched = true;
        } else if (page.titleStemSet.has(tokenStem)) {
            score += 30;
            tokenMatched = true;
        } else if (hasPartialTokenMatch(token, page.titleTokens)) {
            score += 20;
            tokenMatched = true;
        }

        if (page.headingTokenSet.has(token)) {
            score += 24;
            tokenMatched = true;
        } else if (page.headingStemSet.has(tokenStem)) {
            score += 20;
            tokenMatched = true;
        } else if (hasPartialTokenMatch(token, page.headingTokens)) {
            score += 12;
            tokenMatched = true;
        }

        if (page.pathTokenSet.has(token)) {
            score += 18;
            tokenMatched = true;
        } else if (page.pathStemSet.has(tokenStem)) {
            score += 14;
            tokenMatched = true;
        }

        if (page.contentTokenSet.has(token)) {
            score += 10;
            tokenMatched = true;
        } else if (page.contentStemSet.has(tokenStem)) {
            score += 8;
            tokenMatched = true;
        } else if (hasPartialTokenMatch(token, page.contentTokens)) {
            score += 4;
            tokenMatched = true;
        }

        if (tokenMatched) {
            matchedTokens.add(token);
        }
    }

    if (matchedTokens.size > 0) {
        score += Math.round((matchedTokens.size / queryTokens.length) * 50);
    }

    if (
        queryTokens.length > 1 &&
        queryTokens.every((token) => page.searchableText.includes(token))
    ) {
        score += 35;
    }

    return score;
}

export function searchPages(searchQuery) {
    const normalizedQuery = normalizeText(searchQuery);
    const queryTokens = getQueryTokens(searchQuery);

    if (!normalizedQuery || !queryTokens.length) {
        return [];
    }

    return getSearchIndex()
        .map((page) => ({
            ...page,
            score: scorePage(page, queryTokens, normalizedQuery),
        }))
        .filter((page) => page.score > 0)
        .sort((a, b) => b.score - a.score || a.title.localeCompare(b.title));
}
